/**
 * DAILY BURN (Daily Consumption Rate) & Devices Calculation Logic:
 *
 * 1. DAILY BURN Calculation:
 *    - Formula: sum(Total devices per site * Site-specific rate) + AI Chatbot rate
 *    - Site-specific rate (site.rate):
 *        - MSP Mode (underMsp: true):
 *            - Base rate: 15 credits/day/device
 *            - Unity Pro Service: +2 credits/day/device if enabled
 *            - Health AI Service: +1 credit/day/device if enabled
 *            - Max per device: 17 credits/day
 *        - Non-MSP Mode (underMsp: false):
 *            - Unity Pro Service: +2 credits/day/device if enabled
 *            - Health AI Service: +1 credit/day/device if enabled
 *            - Max per device: 2 credits/day
 *    - AI Chatbot: If aiChatbotEnabled is true for the org, a fixed 1 credit/day is deducted (independent of device count).
 *
 * 2. Devices Count:
 *    - Single Site: Sum of accessPoints (AP) + switches.
 *    - Organization: Total sum of devices across all sites in the organization.
 *
 * 3. Relationship Structure:
 *    - Organization (Wallet/MSP Status) ➔ Site (Service Toggles) ➔ Devices (AP/Switch Count)
 *    - This structure allows different sites under the same organization to have different device counts and service combinations.
 *      The system automatically calculates the organization's total daily consumption speed.
 */

/** 
 * Page logic consists of three phases: Initialization, Change Estimation, and Execution & Refresh.
 * 
 * Phase 1: Initialization
 * 1. Subscriptions: Verify current service status (Active/Inactive).
 * 2. Sites: Fetch site names and IDs within the organization.
 * 3. Base Rates: Determine base credits (15 or 0) based on MSP status.
 * 4. Device Counts: Fetch precise device counts per site from API (fallback to site data sum).
 * 5. Dashboard Data: Fetch current wallet balance and total daily usage.

 * Phase 2: Interaction & Change Estimation
 * ServiceChangeModal handles estimations when a user toggles "Activate" or "Deactivate":
 * 1. Remaining Days: Calculate days left in the current month using dayjs (including today).
 * 2. Estimated Deduction/Refund:
 *    - Formula: Device count * Service unit price (1) * remaining days in month.
 *    - Displays as deduction for activation, or refund for deactivation.
 * 3. Daily Burn Rate Impact:
 *    - Change value: Device count * 1.
 *    - Calculation: Current daily burn rate +/- change value = estimated speed after change.

 * Phase 3: Confirmation & Execution
 * 1. Confirmation: User reviews estimation in Modal and clicks "Confirm".
 * 2. Countdown: Trigger CountdownModal (3s), allowing user to cancel.
 * 3. API Invocation: Call handleInvokeApi to send update request.
 * 4. Automatic Refresh: Call init() after successful update.

 * Business Rules Summary:
 * - AI Chatbot (Org level): Fixed 1 credit/day deduction when enabled.
 * - Unity Pro (Site level): 2 credits/day/device deduction based on site device count.
 * - Base Rates: System automatically identifies org threshold deduction via baseRates.
 */

/**
 * APIs used in License Subscriptions:
 * - GET /orgs/:orgId/license/subscriptions.read (or /ents/:entId/orgs/:orgId/license/subscriptions.read)
 * - GET /orgs/:orgId/devices.read (or /ents/:entId/orgs/:orgId/devices.read)
 * - GET /orgs/:orgId/license/dashboard.read (or /ents/:entId/orgs/:orgId/license/dashboard.read)
 * - PUT /orgs/:orgId/license/subscriptions.update (or /ents/:entId/orgs/:orgId/license/subscriptions.update)
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

// material-ui
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularLoader from "components/CircularLoader";
import { FormattedMessage } from "react-intl";
import {
  LICENSE_SERVICE_ACTION_ACTIVE,
  LICENSE_SERVICE_ACTION_DEACTIVE,
  LICENSE_SERVICE_STATUS_ACTIVE,
  LICENSE_SERVICE_STATUS_INACTIVE,
  UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
  UNITY_PRO_SERVICE_KEY,
  UNITY_PRO_SWITCH_DAILY_CREDITS,
} from "const/license";

// Component
import GlobalAlert from "components/Notification";

// Hooks
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Components
import SiteBasedBlock from "./components/SiteBasedBlock";
import ServiceChangeModal from "./components/ServiceChangeModal";

const Subscriptions = () => {
  const theme = useTheme();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch, sendRequest, getData, dataMap } = useDataSourceManager();

  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null); // Subscriptions status (Active/Inactive)
  const [sites, setSites] = useState([]); // List of sites in the organization
  const [baseRates, setBaseRates] = useState(null); // Fee structure (MSP vs non-MSP)
  const [deviceCounts, setDeviceCounts] = useState({}); // Device quantity per site (API provided)
  const [balance, setBalance] = useState(0); // Current wallet credit balance
  const [currentDailyBurn, setCurrentDailyBurn] = useState(0); // Current daily consumption speed

  const [pendingUpdateUuid, setPendingUpdateUuid] = useState(null);
  const [preConfirmSocketId, setPreConfirmSocketId] = useState(null);
  const [preConfirmResult, setPreConfirmResult] = useState(null);
  const [activePreConfirmData, setActivePreConfirmData] = useState(null);

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [modalConfig, setModalConfig] = useState({
    open: false,
    service: null, // Stores information about the service being modified
  });

  // --- UUIDs for API Request Tracking ---
  // Since sendBatch handles multiple requests, unique IDs are used to retrieve specific responses.
  const subUuid = useMemo(() => uuidv4(), []);
  const deviceUuid = useMemo(() => uuidv4(), []);
  const dashUuid = useMemo(() => uuidv4(), []);

  /**
   * Helper to get necessary IDs based on auth context.
   * realmId: Enterprise ID or Org ID (in standalone mode)
   * orgId: Targeted Organization ID
   */
  const getIds = () => ({
    realmId: auth.realm?.id || "",
    orgId: auth.orgs?.id || "",
  });

  // --- API Request Definitions ---
  // Each function returns a configuration object for sendBatch.
  // Logic accounts for 'standalone' vs 'enterprise' mode to select the correct endpoint.

  const fetchLicenseDashboard = useCallback(() => {
    const { realmId, orgId } = getIds();
    const apiKey = standalone
      ? "/orgs/:orgId/license/dashboard.read"
      : "/ents/:entId/orgs/:orgId/license/dashboard.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };
    return {
      apiKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: dashUuid,
    };
  }, [standalone, auth, dashUuid]);

  const fetchDeviceCounts = useCallback(() => {
    const { realmId, orgId } = getIds();
    const apiKey = standalone
      ? "/orgs/:orgId/devices.read"
      : "/ents/:entId/orgs/:orgId/devices.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };
    return {
      apiKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: deviceUuid,
    };
  }, [standalone, auth, deviceUuid]);

  const fetchSubscriptions = useCallback(() => {
    const { realmId, orgId } = getIds();
    const apiKey = standalone
      ? "/orgs/:orgId/license/subscriptions.read"
      : "/ents/:entId/orgs/:orgId/license/subscriptions.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };
    return {
      apiKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: subUuid,
    };
  }, [standalone, auth, subUuid]);

  /**
   * Initialization: Fetches all required data in a single batch to minimize network turns.
   */
  const init = useCallback(async () => {
    setIsLoading(true);

    try {
      const requests = [
        fetchSubscriptions(),
        fetchDeviceCounts(),
        fetchLicenseDashboard(),
      ];

      const batchResult = await sendBatch(requests);
      const { getSendBatchData } = batchResult;

      // Extracting data using the pre-defined UUIDs
      const subRes = getSendBatchData({ requestUuid: subUuid });
      const deviceRes = getSendBatchData({ requestUuid: deviceUuid });
      const dashRes = getSendBatchData({ requestUuid: dashUuid });

      if (subRes?.data) {
        const orgData = subRes.data.org || {};
        const sitesList = orgData.sites || [];

        // Define siteLevelServices if not provided by API
        // id should match the one expected in handleInvokeApi
        const siteLevelServices = [
          {
            id: "unity-pro",
            name: "Nuclias Unity Pro",
            price: `${UNITY_PRO_ACCESS_POINT_DAILY_CREDITS} Credits/device/day`,
          },
        ];

        // Map site subscriptions from the nested 'services' object
        const siteSubscriptions = {};
        sitesList.forEach((site) => {
          siteSubscriptions[site.id] = {
            "unity-pro": site.services?.[UNITY_PRO_SERVICE_KEY]
              ? LICENSE_SERVICE_STATUS_ACTIVE
              : LICENSE_SERVICE_STATUS_INACTIVE,
          };
        });

        setSubscriptionData({
          ...subRes.data,
          siteLevel: siteLevelServices,
          siteSubscriptions,
        });

        if (sitesList.length > 0) {
          setSites(sitesList);
        }

        // Use walletBalance from subRes as a fallback for balance
        if (orgData.walletBalance !== undefined) {
          setBalance((prev) => (prev === 0 ? orgData.walletBalance : prev));
        }
      } else {
        console.warn("[Subscriptions] No subscription data found.");
      }

      // Hardcoded Base Rates
      setBaseRates({
        accessPoint: UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
        switch: UNITY_PRO_SWITCH_DAILY_CREDITS,
      });

      if (deviceRes?.data) {
        const counts = {};
        const deviceList =
          deviceRes.data.devices ||
          (Array.isArray(deviceRes.data)
            ? deviceRes.data
            : deviceRes.data.data);

        if (Array.isArray(deviceList)) {
          deviceList.forEach((d) => {
            const sId = d.info?.siteId || d.siteId;
            if (sId) {
              counts[sId] = (counts[sId] || 0) + 1;
            }
          });

          setDeviceCounts(counts);
        }
      }

      if (dashRes?.data) {
        setBalance(dashRes.data.balance || 0);
        setCurrentDailyBurn(dashRes.data.dailyUsage || 0);
      }
    } catch (error) {
      console.error(
        "[Subscriptions] FATAL ERROR during initialization:",
        error,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    sendBatch,
    subUuid,
    deviceUuid,
    dashUuid,
    fetchSubscriptions,
    fetchDeviceCounts,
    fetchLicenseDashboard,
  ]);

  useEffect(() => {
    if (isAuthReady) {
      init();
    }
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id]);

  // --- UI Event Handlers ---

  const handleOpenModal = (serviceInfo) => {
    setModalConfig({ open: false, service: serviceInfo });
    setIsLoading(true);
    setPreConfirmResult(null);

    const newSocketId = uuidv4();
    setPreConfirmSocketId(newSocketId);

    const { realmId, orgId } = getIds();
    const apiKey = standalone
      ? "/orgs/:orgId/license/service/subscribe/preConfirm"
      : "/ents/:entId/orgs/:orgId/license/service/subscribe/preConfirm";

    const placeholder = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    const params = {
      data: {
        siteId: serviceInfo.siteId,
        service: UNITY_PRO_SERVICE_KEY,
        action:
          serviceInfo.action === "activate"
            ? LICENSE_SERVICE_ACTION_ACTIVE
            : LICENSE_SERVICE_ACTION_DEACTIVE,
      },
    };

    sendRequest(apiKey, newSocketId, placeholder, params);
  };

  const handleCloseModal = () => {
    setModalConfig({ open: false, service: null });
    setActivePreConfirmData(null);
  };

  useEffect(() => {
    if (preConfirmSocketId && dataMap[preConfirmSocketId]) {
      const response = getData(preConfirmSocketId);
      if (response) {
        setPreConfirmResult(response);
        setPreConfirmSocketId(null);
      }
    }
  }, [dataMap, preConfirmSocketId, getData]);

  useEffect(() => {
    if (preConfirmResult) {
      if (preConfirmResult.error || (preConfirmResult.code !== undefined && preConfirmResult.code !== 200)) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: preConfirmResult.message || "Failed to pre-confirm subscription change.",
        });
        setIsLoading(false);
        setPreConfirmResult(null);
        return;
      }

      const data = preConfirmResult.data || {};
      setActivePreConfirmData(data);
      setPreConfirmResult(null);
      setIsLoading(false);
      setModalConfig((prev) => ({ ...prev, open: true }));
    }
  }, [preConfirmResult]);

  /**
   * Final Execution: Called after the internal countdown finishes.
   * Sends the actual update request to the server.
   */
  const handleInvokeApi = useCallback(async () => {
    const service = modalConfig.service;
    if (!service) return;

    // 1. Keep Modal open and start Loading after countdown
    setIsLoading(true);

    try {
      const { realmId, orgId } = getIds();
      const apiKey = standalone
        ? "/orgs/:orgId/license/subscriptions.update"
        : "/ents/:entId/orgs/:orgId/license/subscriptions.update";
      const restfulParams = standalone
        ? { orgId: realmId }
        : { entId: realmId, orgId };

      const uuid = uuidv4();
      setPendingUpdateUuid(uuid);

      // Use sendRequest instead of sendBatch
      sendRequest(apiKey, uuid, restfulParams, {
        data: {
          siteId: service.siteId || "",
          service: UNITY_PRO_SERVICE_KEY,
          action:
            service.action === "activate"
              ? LICENSE_SERVICE_ACTION_ACTIVE
              : LICENSE_SERVICE_ACTION_DEACTIVE,
        },
      });
    } catch (error) {
      console.error("[Subscriptions] API Update Failed:", error);
      setAlertInfo({
        open: true,
        severity: "error",
        message: "An unexpected error occurred while updating subscription.",
      });
      setIsLoading(false);
      // Do not close the modal on error
    }
  }, [modalConfig.service, standalone, auth, sendRequest]);

  // --- Side Effects ---
  /**
   * Listen to dataMap for sendRequest response
   */
  useEffect(() => {
    if (!pendingUpdateUuid) return;

    const response = getData(pendingUpdateUuid);
    if (response) {
      if (
        response.error ||
        (response.code !== undefined && response.code !== 200)
      ) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: response.message || "Failed to update subscription.",
        });
        setIsLoading(false); // Stop loading and show alert on error
        setPendingUpdateUuid(null);
        // Note: Do not close modal on error
        return;
      }

      // Success handling
      if (subscriptionData) {
        const updatedSiteSubscriptions = {
          ...subscriptionData.siteSubscriptions,
        };
        const serviceKey = "unity-pro";
        const action =
          modalConfig.service.action === "activate"
            ? LICENSE_SERVICE_STATUS_ACTIVE
            : LICENSE_SERVICE_STATUS_INACTIVE;

        updatedSiteSubscriptions[modalConfig.service.siteId][serviceKey] =
          action;

        setSubscriptionData((prev) => ({
          ...prev,
          siteSubscriptions: updatedSiteSubscriptions,
        }));
      }

      setPendingUpdateUuid(null);
      // Close modal only after success
      setModalConfig({ open: false, service: null });

      // Re-initialize data (init manages its own loading state)
      init();
    }
  }, [
    dataMap,
    pendingUpdateUuid,
    getData,
    subscriptionData,
    modalConfig.service,
    init,
  ]);

  if (!subscriptionData && isLoading) return <CircularLoader />;

  if (!subscriptionData) {
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography color='error' sx={{ fontWeight: "bold" }}>
          Failed to load subscription data.
        </Typography>
        <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
          Please verify your connection or organizational access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", minHeight: "400px" }}>
      {isLoading && (
        <Box
          sx={{
            position: "fixed", // Changed to fixed to overlay entire screen including Modal
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999, // High zIndex to cover everything
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.6)",
          }}
        >
          <CircularLoader />
        </Box>
      )}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          mb: 3,
          height: "64px",
          mt: -1, // Adjust to align with top of sidebar
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: "#172664",
            fontFamily: theme.typography.fontFamily,
            fontSize: "20px",
            fontWeight: "bold",
          }}
        >
          <FormattedMessage id='subscriptions' />
        </Typography>
      </Stack>

      <Grid container spacing={5}>
        {/* OrganizationLevelBlock is hidden in this version as AI Chatbot is not supported */}
        {/* <Grid item xs={12}>
          <OrganizationLevelBlock
            services={subscriptionData.orgLevel || []}
            onAction={handleOpenModal}
          />
        </Grid> */}
        <Grid item xs={12}>
          <SiteBasedBlock
            sites={sites}
            deviceCounts={deviceCounts}
            siteLevelServices={subscriptionData.siteLevel || []}
            siteSubscriptions={subscriptionData.siteSubscriptions || {}}
            onAction={handleOpenModal}
          />
        </Grid>
      </Grid>

      <ServiceChangeModal
        open={modalConfig.open}
        handleClose={handleCloseModal}
        serviceName={modalConfig.service?.name}
        siteName={modalConfig.service?.siteName}
        devices={modalConfig.service?.devices}
        isOrgLevel={!!modalConfig.service?.scope}
        actionType={
          modalConfig.service?.action === "activate" ? "Activate" : "Deactivate"
        }
        rateText={modalConfig.service?.price}
        onConfirm={handleInvokeApi}
        currentDailyBurn={currentDailyBurn}
        balance={balance}
        baseRates={baseRates}
        activePreConfirmData={activePreConfirmData}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Subscriptions;
